/**
 * Formulario 024 del MSP (Consentimiento informado, Acuerdo Ministerial 5316) llenado con un consentimiento de Donna.
 * Las dos hojas (anverso y reverso) son una reproducción celda por celda del Excel oficial (`datos/form024.json`).
 * Anverso: datos del establecimiento y usuario, consentimiento informado (procedimiento, beneficios, riesgos, alternativas).
 * Reverso: declaración, negativa y revocatoria, con las firmas de quien corresponda según el estado del consentimiento.
 * Anexo: el texto completo tal como se firmó en Donna (lo que no cabe en los renglones del formulario sigue ahí).
 */
import { useEffect, useState } from "react";
import { api, leerSesion, type HallazgoApi } from "../api.ts";
import {
  CIE10, norm, partirApellidos, partirNombres, edadConCondicion, fechaEnTexto, partesDeConsentimiento, diagnosticosDeConsentimiento, tituloConsentimiento, descripcionCie,
} from "../../../dominio/src/index.ts";
import { Casilla, Seccion033 } from "../componentes/FormularioOficial.tsx";
import { PlantillaHoja, anchoDe, ajusteTexto, type Relleno } from "../componentes/PlantillaHoja.tsx";
import { useClinica } from "../clinica.ts";
import { AvisoError, hoy } from "./comun.tsx";
import type { Expediente } from "./Expediente.tsx";

interface Consentimiento {
  id: string; paciente_id: string; tipo: string; estado: "firmado" | "negado" | "revocado" | "previo"; texto: string; otorgante: string; otorgante_doc: string; firma: string | null; motivo: string; creado: string;
  procedimientos: string[]; profesional_nombre: string; registro_profesional: string;
}

const zona = "America/Guayaquil";
const fechaTxt = (iso: string) => fechaEnTexto(new Date(iso).toLocaleDateString("en-CA", { timeZone: zona }));
const horaTxt = (iso: string) => new Date(iso).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: zona });
const institucionGuardada = (clinicaId: string) => { try { const s = localStorage.getItem(`donna.f033.${clinicaId}`); return s ? String(JSON.parse(s).institucion ?? "") : ""; } catch { return ""; } };

/** Lo que cabe en una celda del formulario: si el texto es más largo se corta y se remite al anexo. */
function cabeEn(texto: string, anchoPx: number, renglones: number): { texto: string; pt: number; por: number } {
  const a = ajusteTexto(texto, anchoPx, renglones);
  if (texto.length <= a.max) return { texto, pt: a.pt, por: a.por };
  const corte = texto.slice(0, Math.max(0, a.max - 30)).replace(/\s+\S*$/, "");
  return { texto: `${corte} … (completo en el anexo)`, pt: a.pt, por: a.por };
}

export function Formulario024({ pacienteId, consentimientoId, volver }: { pacienteId: string; consentimientoId: string; volver: () => void }) {
  const [c, setC] = useState<Consentimiento | null>(null);
  const [e, setE] = useState<Expediente | null>(null);
  const [error, setError] = useState<unknown>(null);
  const clinica = useClinica();
  useEffect(() => {
    if (consentimientoId.startsWith("previo-")) {
      // consentimiento aún sin firmar: el formulario sale prellenado con el texto del plan y los datos del paciente, listo para firmar
      const tipo = consentimientoId.slice(7);
      api<{ otorgante: { nombre: string; doc: string }; lista: { tipo: string; procedimientos: string[]; texto: string }[] }>("GET", `/api/pacientes/${pacienteId}/consentimientos`).then((r) => {
        const x = r.lista.find((l) => l.tipo === tipo);
        if (!x) throw new Error("No se encontró ese consentimiento.");
        const u = leerSesion()?.usuario;
        setC({ id: consentimientoId, paciente_id: pacienteId, tipo, estado: "previo", texto: x.texto, otorgante: r.otorgante.nombre, otorgante_doc: r.otorgante.doc, firma: null, motivo: "",
          creado: new Date().toISOString(), procedimientos: x.procedimientos, profesional_nombre: u?.nombre ?? "", registro_profesional: u?.registro ?? "" });
      }).catch(setError);
    } else api<Consentimiento>("GET", `/api/consentimientos/${consentimientoId}`).then(setC).catch(setError);
    api<Expediente>("GET", `/api/pacientes/${pacienteId}/expediente`).then(setE).catch(setError);
  }, [pacienteId, consentimientoId]);
  useEffect(() => {
    document.body.classList.add("modo-expediente");
    const antes = document.title;
    if (c && e) document.title = `Formulario 024 - HC ${e.paciente.hc} - ${e.paciente.apellidos} ${e.paciente.nombres}`.trim();
    return () => { document.body.classList.remove("modo-expediente"); document.title = antes; };
  }, [c, e]);

  if (error) return <AvisoError error={error} />;
  if (!c || !e) return <p className="muted">Preparando el formulario 024…</p>;
  const p = e.paciente;
  const partes = partesDeConsentimiento(c.texto);
  const titulo = partes.titulo || tituloConsentimiento(c.tipo);
  const [ape1, ape2] = partirApellidos(p.apellidos), [nom1, nom2] = partirNombres(p.nombres);
  const ec = edadConCondicion(p.nacimiento, hoy());
  const rep = p.representante;
  // quien firma es el representante legal si no es el propio paciente (su nombre y primer apellido no están en lo que firmó)
  const palabras = [p.nombres.split(/\s+/)[0], p.apellidos.split(/\s+/)[0]].filter(Boolean).map((w) => norm(w));
  const otorgaRepresentante = !palabras.every((w) => norm(c.otorgante).includes(w));
  const hs = e.odontograma.hallazgos as HallazgoApi[];
  // solo los diagnósticos de la especialidad de este consentimiento (cirugía, endodoncia, odontología general…)
  const dx = diagnosticosDeConsentimiento(c.tipo, hs, e.historia.examen.regiones, c.procedimientos).slice(0, 4);
  const sesion = leerSesion();
  const establecimiento = [clinica?.nombre ?? sesion?.usuario.clinica ?? "", clinica?.ruc ? `RUC ${clinica.ruc}` : ""].filter(Boolean).join(" · ");
  // la fecha del consentimiento que se revoca: el último firmado de ese tipo antes de la revocatoria
  const firmadoAntes = c.estado === "revocado" ? e.consentimientos.filter((k) => k.tipo === c.tipo && k.estado === "firmado" && k.creado <= c.creado).sort((m, o) => o.creado.localeCompare(m.creado))[0] : null;
  const nombrePaciente = `${p.nombres} ${p.apellidos}`.trim();
  const parentesco = otorgaRepresentante ? ([rep?.nombre && norm(rep.nombre) === norm(c.otorgante) ? rep.parentesco : ""].filter(Boolean).join("") || "") : "";

  /* ---------------- anverso ---------------- */
  const a: Relleno[] = [
    { ref: "A4", texto: clinica ? clinica.institucion || institucionGuardada(clinica.id) : "" }, { ref: "P4", texto: establecimiento, pt: 7.5 }, { ref: "AK4", texto: p.doc ?? "", alinear: "c" }, { ref: "BC4", texto: String(p.hc), alinear: "c" },
    { ref: "A7", texto: ape1 }, { ref: "R7", texto: ape2 }, { ref: "AF7", texto: nom1 }, { ref: "AP7", texto: nom2 },
    { ref: "BA7", texto: p.sexo === "M" || p.sexo === "F" ? p.sexo : "", alinear: "c" }, { ref: "BD7", texto: ec ? String(ec.valor) : "", alinear: "c" },
    ...(["H", "D", "M", "A"] as const).filter((k) => ec?.condicion === k).map((k) => ({ ref: `${["BH", "BJ", "BL", "BN"][["H", "D", "M", "A"].indexOf(k)]}7`, x: true, alinear: "c" as const })),
    { ref: "S10", texto: titulo.toUpperCase(), pt: 8, negrita: true },
    { ref: "G11", texto: "Odontología" }, { ref: "AZ11", x: true, alinear: "c" },
    { ref: "I12", texto: dx.map((d) => `${d.texto} (${d.donde})`).join("; "), pt: 6.5, ajustar: true }, { ref: "BE12", texto: [...new Set(dx.map((d) => d.cie))].join(", "), pt: 7, negrita: true, ajustar: true },
    { ref: "X13", texto: partes.procedimientos.length ? partes.procedimientos.join("; ") : titulo, pt: 7.5, ajustar: true },
  ];
  const bloque = (ref: string, columnas: number, filas: number, texto: string) => {
    if (!texto) return;
    const t = cabeEn(texto, anchoDe("a024", ref, columnas), filas);
    a.push({ ref, columnas, filas, texto: t.texto, renglones: true, pt: t.pt, porRenglon: t.por });
  };
  bloque("L14", 56, 2, partes.naturaleza);
  bloque("V36", 46, 1, partes.objetivos);
  bloque("L16", 56, 2, partes.como);
  bloque("V35", 46, 1, partes.duracion);
  bloque("V37", 46, 1, partes.riesgosFrecuentes.join("; "));
  bloque("V38", 46, 1, partes.riesgosPocoFrecuentes.join("; "));
  bloque("A40", 67, 2, partes.personales.join(" "));
  bloque("V42", 46, 1, partes.alternativas);
  bloque("V43", 46, 1, partes.manejo);
  bloque("V44", 46, 1, partes.consecuencias);

  /* ---------------- reverso ---------------- */
  const b: Relleno[] = [];
  const fechaC = fechaTxt(c.creado), horaC = horaTxt(c.creado);
  /** Una sección de firmas: paciente, profesional y, si firmó otra persona, su representante legal con el parentesco. */
  const firmas = (f: { pac: number; prof: number; rep: number; par: number }) => {
    const imagen = c.firma ? <img src={c.firma} alt={`Firma: ${c.otorgante}`} /> : undefined;
    if (otorgaRepresentante) {
      b.push({ ref: `A${f.pac}`, texto: nombrePaciente, pt: 8 }, { ref: `AG${f.pac}`, texto: p.doc ?? "", pt: 8, alinear: "c" });
      b.push({ ref: `A${f.rep}`, texto: c.otorgante, pt: 8 }, { ref: `AG${f.rep}`, texto: c.otorgante_doc, pt: 8, alinear: "c" }, { ref: `A${f.par}`, texto: parentesco, pt: 8 });
      if (imagen) b.push({ ref: `AX${f.rep}`, hijo: imagen });
    } else {
      b.push({ ref: `A${f.pac}`, texto: c.otorgante, pt: 8 }, { ref: `AG${f.pac}`, texto: c.otorgante_doc, pt: 8, alinear: "c" });
      if (imagen) b.push({ ref: `AX${f.pac}`, hijo: imagen });
    }
    b.push({ ref: `A${f.prof}`, texto: c.profesional_nombre, pt: 8 }, { ref: `AF${f.prof}`, texto: c.registro_profesional ? `MSP ${c.registro_profesional}` : "", pt: 8, alinear: "c" });
  };
  const motivo = c.motivo ? `Motivo registrado: ${c.motivo}` : "";
  if (c.estado === "firmado" || c.estado === "previo") {
    if (c.estado === "firmado") b.push({ ref: "L2", texto: fechaC, alinear: "c" }, { ref: "AY2", texto: horaC, alinear: "c" });
    firmas({ pac: 11, prof: 14, rep: 20, par: 23 });
  } else if (c.estado === "negado") {
    b.push({ ref: "L28", texto: fechaC, alinear: "c" });
    firmas({ pac: 33, prof: 36, rep: 42, par: 45 });
    if (motivo) b.push({ ref: "A32", columnas: 67, texto: motivo, pt: 7.5 });
  } else {
    firmas({ pac: 59, prof: 62, rep: 68, par: 71 });
    b.push({ ref: "A58", columnas: 67, texto: [`Consentimiento otorgado el ${firmadoAntes ? fechaTxt(firmadoAntes.creado) : "........"}; revocado el ${fechaC} a las ${horaC}.`, motivo].filter(Boolean).join(" "), pt: 7.5, negrita: true });
  }

  return (
    <div className="expediente of">
      <div className="exp-barra no-imprimir">
        <button className="btn" onClick={volver}>← Volver al paciente</button>
        <button className="btn pri" onClick={() => window.print()}>Imprimir o guardar en PDF</button>
        <small className="muted">Anverso, reverso y anexo con el texto firmado en Donna. Elige «Guardar como PDF» como destino si lo quieres en archivo.</small>
      </div>
      <PlantillaHoja nombre="a024" rellenos={a} etiqueta="Formulario 024, anverso" />
      <PlantillaHoja nombre="b024" rellenos={b} etiqueta="Formulario 024, reverso" />
      <article className="of-hoja" aria-label="Formulario 024, anexo">
        <Seccion033 letra="+" titulo={c.estado === "previo" ? `ANEXO · TEXTO COMPLETO DEL CONSENTIMIENTO · HC ${p.hc} · ${nombrePaciente}` : `ANEXO · TEXTO COMPLETO TAL COMO SE FIRMÓ EN DONNA · ${fechaC} ${horaC} · HC ${p.hc} · ${nombrePaciente}`}>
          <div className="of-lectura" style={{ fontSize: 8.5, whiteSpace: "pre-wrap" }}>{c.texto}</div>
          <div className="of-fila"><span className="of-rot">OTORGANTE:</span><b>{c.otorgante}</b>{c.otorgante_doc && <><span className="of-rot">C.I.:</span><b className="mono">{c.otorgante_doc}</b></>}<Casilla marcada={c.estado === "firmado"} etiqueta="Firmado" /><span className="of-rot">{c.estado === "firmado" ? "FIRMADO EN DONNA" : c.estado === "negado" ? "NEGADO EN DONNA" : "REVOCADO EN DONNA"}</span></div>
        </Seccion033>
        <footer className="of-pie"><p>Formulario 024 (modelo del Acuerdo Ministerial 5316) llenado con Donna el {fechaEnTexto(hoy())}. Documento confidencial: contiene datos de salud protegidos por la Ley Orgánica de Protección de Datos Personales.</p></footer>
      </article>
    </div>
  );
}
